/**
 * useDocumentItemExtFields — Dokumenty: pola dodatkowe pozycji dokumentu (Fala B3,
 * `docs/refactor/DOCUMENT-ITEMS-EXT-FIELDS.md`).
 *
 * Pobiera definicje pól modułu systemowego `document_items` (encja pomocnicza,
 * Fala B1 — `isHelperEntity: true`, brak core fields) przez
 * `GET /system-modules/document_items/fields`. Ten sam wzorzec co
 * `siblingFieldsQuery` w `AddSystemFieldModal.tsx` i `useKartotekaRenderField.tsx`
 * (queryKey `['system-module-fields', 'document_items']` — cache WSPÓLNY ze
 * wszystkimi konsumentami tego modułu, staleTime 60 s).
 *
 * Zwraca WYŁĄCZNIE pola ŻYWE (`!isCore && gcrecord === null`) — moduł ma dziś
 * `coreFields: []`, więc filtr `isCore` jest defensywny, nie krytyczny.
 *
 * Brak modułu zarejestrowanego u tenanta / brak dodanych pól → `fields: []` —
 * konsumenci (editor/sekcja/edytor układu kolumn) renderują wtedy
 * IDENTYCZNIE jak przed Falą B3 (zero regresji — deskryptory tylko wbudowane).
 *
 * Permission: brak bramki `can()` na endpoincie (config metadata modułu, nie
 * dane rekordu) — dowolny zalogowany user tenanta widzi definicje pól.
 *
 * Błąd zapytania (`isError`/`error`, finding [LOW] przeglądu frontendu) NIE blokuje
 * renderu — `fields` degraduje do `[]` tak samo jak przy braku modułu; konsumenci,
 * którzy chcą diagnostyki, mogą pokazać `error` (patrz `DocumentItemsColumnsCanvas`).
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import type { ExtFieldValueInputField } from '@/components/system-modules/ExtFieldValueInput';
import { api } from '@/lib/api';

const MODULE_SLUG = 'document_items';

/**
 * Kształt pojedynczego pola z odpowiedzi GET /system-modules/:slug/fields —
 * nadzbiór `ExtFieldValueInputField` (duck-typing, przekazywany do
 * `<ExtFieldValueInput>` bez adaptera) + metadane potrzebne do filtrowania
 * core/skasowanych (mirror `EngineFormField` z silnika formatek dokumentów).
 */
export interface DocumentItemExtFieldDef extends ExtFieldValueInputField {
  id: string;
  isCore: boolean;
  gcrecord: string | null;
}

interface FieldsResponse {
  fields: DocumentItemExtFieldDef[];
}

export interface UseDocumentItemExtFieldsResult {
  /** Pola ext ŻYWE, w kolejności `sortOrder` zwróconej przez API. */
  fields: DocumentItemExtFieldDef[];
  isLoading: boolean;
  /** `true` gdy zapytanie o pola ext zakończyło się błędem (np. 500/sieć). Po nieudanym
   *  ODŚWIEŻENIU `fields` nadal niesie ostatnie udane dane, a bez żadnych danych jest `[]` —
   *  konsument, który przy błędzie chce wyłącznie kolumn wbudowanych, odrzuca je sam
   *  (robi to `useDocumentItemColumnsModel`). */
  isError: boolean;
  /** Przyczyna błędu (patrz `isError`) — `null` gdy zapytanie się powiodło/jeszcze trwa. */
  error: unknown;
}

export interface UseDocumentItemExtFieldsOptions {
  /**
   * `false` = bez zapytania do API (dla kreatora osadzonego w panelu ITE, gdzie endpointy tenanta
   * odpowiadają 401) — domyślnie `true`. Wyłączone zapytanie nadal oddaje dane, które pamięć
   * podręczna (klucz wspólny z innymi konsumentami modułu) już trzyma, więc `fields` nie jest
   * wtedy gwarantowanie puste; konsument, który potrzebuje „bez pól”, sam je odrzuca (robi to
   * `useDocumentItemColumnsModel` przy `disabled`).
   */
  enabled?: boolean;
}

export function useDocumentItemExtFields(
  options: UseDocumentItemExtFieldsOptions = {},
): UseDocumentItemExtFieldsResult {
  const query = useQuery({
    queryKey: ['system-module-fields', MODULE_SLUG],
    enabled: options.enabled ?? true,
    queryFn: async () => {
      const res = await api.get<FieldsResponse>(`/system-modules/${MODULE_SLUG}/fields`);
      return res.data.fields;
    },
    staleTime: 60_000,
  });

  const fields = useMemo(
    () => (query.data ?? []).filter((f) => !f.isCore && f.gcrecord === null),
    [query.data],
  );

  return { fields, isLoading: query.isLoading, isError: query.isError, error: query.error };
}
